import { useLocation } from "@tanstack/react-router";

import { extractHeadings, findDocLocation } from "@/landing/docs/docs-nav";
import { MarkdownRenderer } from "@/landing/docs/MarkdownRenderer";
import { Eyebrow, SectionHeading } from "@/landing/system/layout";

type DocPageProps = {
  title: string;
  description?: string;
  content: string;
};

/**
 * One docs page: the group as eyebrow, the title and description, then the
 * markdown in Grid prose. Wide screens get an "On this page" list of the h2s.
 */
export function DocPage({ title, description, content }: DocPageProps) {
  const section = findDocLocation(useLocation().pathname ?? "")?.section.title;
  const toc = extractHeadings(content).filter((heading) => heading.depth === 2);

  return (
    <div className="flex items-start gap-[clamp(40px,4vw,64px)]">
      <article className="max-w-[46rem] min-w-0 flex-1">
        <SectionHeading
          as="h1"
          size="page"
          eyebrow={section ?? "Docs"}
          title={title}
          sub={description}
        />
        <div className="g-prose mt-10 border-t border-(--g-rule-soft) pt-10">
          <MarkdownRenderer content={content} />
        </div>
      </article>

      {toc.length >= 3 && (
        <nav
          aria-label="On this page"
          className="sticky top-[calc(var(--header-height)+40px)] hidden max-h-[calc(100dvh-var(--header-height)-80px)] w-[200px] flex-none overflow-y-auto xl:block"
        >
          <Eyebrow className="mb-4">On this page</Eyebrow>
          <ul className="m-0 list-none space-y-2.5 p-0">
            {toc.map((heading) => (
              <li key={heading.id}>
                <a
                  href={`#${heading.id}`}
                  className="block text-[13.5px] leading-[1.4] text-(--g-ink-2) no-underline transition-colors hover:text-(--g-ink)"
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
